import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send the session_token cookie on every request so logged-in viewers (with an active pass) skip the paywall.
axios.defaults.withCredentials = true;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---- Auth ----
export const exchangeSession = async (session_id) => {
  const { data } = await axios.post(`${API}/auth/session`, { session_id });
  return data;
};

export const getMe = async () => {
  const { data } = await axios.get(`${API}/auth/me`);
  return data;
};

export const logout = async () => {
  await axios.post(`${API}/auth/logout`, {});
};

export const getAppleConfig = async () => {
  try {
    const { data } = await axios.get(`${API}/auth/apple/config`);
    return !!data.enabled;
  } catch {
    return false;
  }
};

// Full-page redirect entry point for Sign in with Apple (backend handles the OAuth dance).
export const appleStartUrl = `${API}/auth/apple/start`;

// ---- Payments ----
export const createCheckout = async (origin_url) => {
  const { data } = await axios.post(`${API}/payments/checkout`, { origin_url });
  return data;
};

export const getPaymentStatus = async (session_id) => {
  const { data } = await axios.get(`${API}/payments/status/${session_id}`);
  return data;
};

// ---- Admin ----
export const getDealerStats = async () => {
  const { data } = await axios.get(`${API}/admin/dealer-stats`);
  return data;
};

export const addPartner = async (dealer_key) => {
  const { data } = await axios.post(`${API}/admin/partners`, { dealer_key });
  return data;
};

export const removePartner = async (dealer_key) => {
  const { data } = await axios.post(`${API}/admin/partners/remove`, { dealer_key });
  return data;
};


export const analyzeContract = async (file, onUpdate, priorFile) => {
  const form = new FormData();
  form.append("file", file);
  if (priorFile) form.append("prior_file", priorFile);
  const { data } = await axios.post(`${API}/analyze`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  const jobId = data.job_id;
  if (!jobId) return data;

  // poll until the analysis job reaches a terminal state (tight interval — analysis is ~15s now)
  const maxAttempts = 260; // ~6.5 min max
  let transientErrors = 0;
  for (let i = 0; i < maxAttempts; i++) {
    await sleep(i === 0 ? 1200 : 1500);
    let status;
    try {
      const resp = await axios.get(`${API}/analyze/${jobId}`, { timeout: 30000 });
      status = resp.data;
      transientErrors = 0;
    } catch (e) {
      const code = e?.response?.status;
      // A real client error (incl. 404 = job expired) is terminal — surface it.
      if (code && code < 500) throw e;
      // Network blip / timeout / 5xx: tolerate a few consecutive failures before giving up so a
      // single transient error never aborts an in-flight analysis.
      if (++transientErrors <= 4) continue;
      throw e;
    }
    if (status.status === "processing") continue;
    if (status.status === "error") {
      const err = new Error(status.detail || "Analysis failed.");
      err.handled = true;
      throw err;
    }
    // Two-stage flow: render the partial payload (core results + dealer foundation price) right away,
    // then keep polling until the background marketplace search finishes.
    if (status.search_in_progress) {
      if (onUpdate) onUpdate(status);
      continue;
    }
    return status; // final (search complete) or a gate
  }
  throw new Error("Analysis timed out. Please try again.");
};

export const regenerateBuyerScript = async (result, acceptedIndices) => {
  const { data } = await axios.post(`${API}/buyer-script`, {
    result,
    accepted_addon_indices: acceptedIndices,
  });
  return data;
};

export const sendEmail = async (recipient_email, subject, html_content) => {
  const { data } = await axios.post(`${API}/send-email`, {
    recipient_email,
    subject,
    html_content,
  });
  return data;
};
